import { EventKey } from '../../player/enums/event-key';
import { formatCapabilities } from '../../player/modules/telemetry-utils';
import { getDeviceCapabilities } from '../../capability';
/**
 * When applied to a Player, periodically gathers data
 * and triggers the `STATS_FOR_NERDS_CHANGED` event.
 * This event can be used to render Stats for Nerds in the UI
 * by platform specific renderers.
 */
export const WithStatsForNerdsDataGatherer = (Base) => {
    return class PlayerWithStatsForNerds extends Base {
        #statsForNerdsVisible = false;
        #events = [
            EventKey.CURRENT_POSITION_CHANGED,
            EventKey.PLAYBACK_STATE_CHANGED,
            EventKey.PLAYER_STATE_CHANGED,
            EventKey.AVAILABLE_AUDIO_TRACKS_CHANGED,
            EventKey.AVAILABLE_SUBTITLES_TRACKS_CHANGED,
            EventKey.SELECTED_AUDIO_TRACK_CHANGED,
            EventKey.SELECTED_SUBTITLES_TRACK_CHANGED,
            EventKey.QUALITY_CHANGED,
        ];
        #triggerStatsForNerdsChanged = async () => {
            const [deviceInfo, capabilities, bufferInfo,] = await Promise.all([
                this.adapter.getDeviceInfo(),
                getDeviceCapabilities(),
                this.adapter.getBufferInformation(),
            ]);
            const { deviceName, deviceOs, deviceOsVersion, } = deviceInfo ?? {};
            const instance = this;
            instance.triggerEvent({
                type: EventKey.STATS_FOR_NERDS_CHANGED,
                statsForNerdsVisible: this.#statsForNerdsVisible,
                deviceInfo: {
                    os: deviceOs,
                    osVersion: deviceOsVersion,
                    name: deviceName,
                },
                sdkVersion: __INJ.SDK_VERSION_JS,
                playerName: this.adapter.playerName,
                playerVersion: this.adapter.playerVersion,
                capabilities: formatCapabilities(capabilities),
                assetType: this.assetType ?? undefined,
                // streamType todo
                // videoResolution todo
                playbackState: this.currentState,
                duration: this.duration ?? undefined,
                position: this.currentPosition ?? 0,
                bufferLength: bufferInfo ? {
                    video: bufferInfo.videoForward ?? 0,
                    audio: bufferInfo.audioForward ?? 0,
                } : undefined,
                bitrate: this._bitrate ?? undefined,
                // startupTime todo
                // latency todo
                selectedAudioTrack: this.selectedAudioTrack ?? undefined,
                selectedSubtitlesTrack: this.selectedSubtitlesTrack ?? undefined,
            });
        };
        #attachEvents = () => {
            this.#events.forEach((event) => {
                // @ts-expect-error signature
                this.on(event, this.#triggerStatsForNerdsChanged);
            });
        };
        #detachEvents = () => {
            this.#events.forEach((event) => {
                // @ts-expect-error signature
                this.off(event, this.#triggerStatsForNerdsChanged);
            });
        };
        get isStatsForNerdsEnabled() {
            return this.#statsForNerdsVisible;
        }
        setStatsForNerds(enabled) {
            if (this.#statsForNerdsVisible && !enabled) {
                this.#detachEvents();
            }
            else if (!this.#statsForNerdsVisible && enabled) {
                this.#attachEvents();
            }
            this.#statsForNerdsVisible = enabled;
            this.#triggerStatsForNerdsChanged();
        }
        destroy() {
            this.#detachEvents();
            return super.destroy();
        }
    };
};
